import { useQuery } from '@tanstack/react-query';
import { Card, Col, Flex, Row, Segmented, Statistic, Tag, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import { useCursorList } from '../../lib/use-cursor-list';
import { formatDate, orDash } from '../../lib/format';
import {
  listRegistrationCases,
  registrationSummary,
  type RegistrationCase,
  type RegistrationStageCode,
} from './api';
import { STAGE_LABEL, STAGE_ORDER, overdueTone, stageTone, STAGE_STATUS_LABEL } from './registration-status';

const { Text } = Typography;

/**
 * Registration aging — P2's acceptance criterion.
 *
 * The question this answers is not "how many bikes are in registration" but
 * "which one has nobody touched, and at which gate". Hence the counters are per
 * GATE and clicking one drills straight into that gate's cases rather than into
 * a chart.
 */
type View = 'overdue' | 'all' | RegistrationStageCode;

export function RegistrationDashboard(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [view, setView] = useState<View>('overdue');

  // The tiles count every open case on the server, so they agree with each
  // other and do not stop at the table's first page.
  const { data: summary } = useQuery({
    queryKey: ['registration-cases', 'summary'],
    queryFn: registrationSummary,
  });

  const { rows, isFetching, hasMore, loadMore } = useCursorList(['registration-cases', view, search], (page) =>
    listRegistrationCases({
      ...page,
      q: search,
      ...(view === 'overdue' ? { overdue: true } : {}),
      ...(STAGE_ORDER.includes(view as RegistrationStageCode)
        ? { stage: view as RegistrationStageCode }
        : {}),
    }),
  );

  const gate = (stage: RegistrationStageCode): { waiting: number; overdue: number } =>
    summary?.gates.find((g) => g.stage === stage) ?? { waiting: 0, overdue: 0 };

  const emptyFor = (): string => {
    if (search.length > 0) return t('registration.noResults');
    if (view === 'overdue') return t('registration.emptyOverdue');
    if (view === 'all') return t('registration.emptyAll');
    return t('registration.emptyGate', { gate: STAGE_LABEL[view] });
  };

  return (
    <Flex vertical gap={16}>
      <Row gutter={[12, 12]}>
        {STAGE_ORDER.map((stage) => {
          const { waiting, overdue: late } = gate(stage);
          return (
            <Col xs={24} sm={8} key={stage}>
              <Card
                hoverable
                onClick={() => {
                  setView(stage);
                }}
                styles={{ body: { padding: 16 } }}
              >
                <Statistic
                  title={t('registration.waitingAt', { gate: STAGE_LABEL[stage] })}
                  value={waiting}
                  valueStyle={late > 0 ? { color: 'var(--negative)' } : undefined}
                  suffix={
                    late > 0 ? (
                      <Text type="danger" style={{ fontSize: 14 }}>
                        {t('registration.lateCount', { count: late })}
                      </Text>
                    ) : undefined
                  }
                />
              </Card>
            </Col>
          );
        })}
      </Row>

      <Segmented<View>
        value={view}
        onChange={(value) => {
          setView(value);
        }}
        size="large"
        block
        options={[
          { value: 'overdue', label: t('registration.viewOverdue') },
          ...STAGE_ORDER.map((stage) => ({ value: stage, label: STAGE_LABEL[stage] })),
          { value: 'all', label: t('registration.viewAll') },
        ]}
      />

      <ListPage<RegistrationCase>
        title={t('registration.title')}
        searchPlaceholder={t('registration.search')}
        onSearch={(value) => {
          setSearch(value);
        }}
        loading={isFetching}
        emptyDescription={emptyFor()}
        columns={[
          {
            title: t('registration.fields.caseNo'),
            dataIndex: 'caseNo',
            width: 175,
            render: (value: string) => <span className="tabular">{value}</span>,
          },
          {
            title: t('vehicles.fields.chassisNo'),
            dataIndex: 'chassisNo',
            width: 185,
            render: (value: string, row: RegistrationCase) => (
              <Flex vertical gap={2}>
                <Text>{value}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {orDash(row.model)}
                </Text>
              </Flex>
            ),
          },
          { title: t('sales.fields.customer'), dataIndex: 'customerName' },
          {
            title: t('registration.fields.atGate'),
            dataIndex: 'currentStage',
            width: 200,
            render: (stage: RegistrationStageCode | null, row: RegistrationCase) => {
              if (stage === null) return <Tag color="success">{t('registration.allDone')}</Tag>;
              const detail = row.stages.find((s) => s.stage === stage);
              return (
                <Flex gap={4} wrap>
                  <Tag color={stageTone(detail?.status ?? 'PENDING')}>{STAGE_LABEL[stage]}</Tag>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {STAGE_STATUS_LABEL[detail?.status ?? 'PENDING']}
                  </Text>
                </Flex>
              );
            },
          },
          {
            title: t('registration.fields.overdue'),
            dataIndex: 'worstOverdueDays',
            width: 130,
            align: 'right' as const,
            render: (days: number | null) =>
              days === null ? (
                <Text type="secondary">{t('registration.onTime')}</Text>
              ) : (
                <Text type={overdueTone(days)} strong>
                  {t('registration.daysLate', { count: days })}
                </Text>
              ),
          },
          {
            title: t('registration.fields.owner'),
            dataIndex: 'ownerName',
            width: 150,
            // An unowned case is the one that sits. Say so rather than dashing it.
            render: (value: string | null) =>
              value === null ? <Text type="warning">{t('registration.unowned')}</Text> : value,
          },
          {
            title: t('registration.fields.opened'),
            dataIndex: 'openedAt',
            width: 140,
            render: (value: string) => formatDate(value),
          },
        ]}
        rows={rows}
        hasMore={hasMore}
        onLoadMore={loadMore}
        rowKey={(row) => row.uid}
        onRowClick={(row) => {
          void navigate(`/registration/${row.uid}`);
        }}
        renderMobileRow={(row) => (
          <Flex vertical gap={4}>
            <Flex justify="space-between" align="center" gap={8}>
              <Text strong>{orDash(row.customerName)}</Text>
              {row.currentStage === null ? (
                <Tag color="success" style={{ marginInlineEnd: 0 }}>
                  {t('registration.allDone')}
                </Tag>
              ) : (
                <Tag
                  color={stageTone(row.stages.find((s) => s.stage === row.currentStage)?.status ?? 'PENDING')}
                  style={{ marginInlineEnd: 0 }}
                >
                  {STAGE_LABEL[row.currentStage]}
                </Tag>
              )}
            </Flex>
            <Flex justify="space-between" gap={8}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {row.chassisNo}
              </Text>
              {row.worstOverdueDays !== null && (
                <Text type={overdueTone(row.worstOverdueDays)} strong>
                  {t('registration.daysLate', { count: row.worstOverdueDays })}
                </Text>
              )}
            </Flex>
          </Flex>
        )}
      />
    </Flex>
  );
}
